import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { DualScoreAdjustment } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save dualScoreAdjustment data.
 */
@Injectable()
export class DualScoreAdjustmentService {
    public static get INCLUDE_TEAM(): string {
        return 'team';
    }

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all dualScoreAdjustments for the specified event
     */
    public getIndex(
        eventId: string,
        dualId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<DualScoreAdjustment[]> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, dualId, null, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const included: any = response.included;
                const dualScoreAdjustments: DualScoreAdjustment[] = [];
                const bodyData: any = response.data || [];
                for (const dualScoreAdjustment of bodyData) {
                    dualScoreAdjustments.push((new DualScoreAdjustment()).deserialize(dualScoreAdjustment, included));
                }
                return dualScoreAdjustments;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(
        eventId: string,
        dualId: string,
        dualScoreAdjustmentId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<DualScoreAdjustment> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, dualId, dualScoreAdjustmentId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new DualScoreAdjustment().deserialize(response))));
    }

    /**
     * Add or update the dualScoreAdjustment
     */
    public save(
        dualScoreAdjustment: DualScoreAdjustment,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<DualScoreAdjustment> {
        queryParams = this.appendGetFields(queryParams);
        let response: Observable<IJsonApiResponse>;
        const dualScoreAdjustmentId: string = !dualScoreAdjustment.isNew ? dualScoreAdjustment.id : null;
        const apiRequestUrl: string = this.getApiUrl(
            dualScoreAdjustment.eventId,
            dualScoreAdjustment.dualId,
            dualScoreAdjustmentId,
            includes,
            queryParams,
        );

        if (!dualScoreAdjustment.isNew) {
            // Update existing DualScoreAdjustment
            response = this.http.patch(apiRequestUrl, dualScoreAdjustment.serialize());
        } else {
            // Add a new DualScoreAdjustment
            response = this.http.post(apiRequestUrl, dualScoreAdjustment.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new DualScoreAdjustment()).deserialize(response)));
    }

    /**
     * Delete the specified dualScoreAdjustment.
     */
    public delete(eventId: string, dualId: string, dualScoreAdjustmentId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, dualId, dualScoreAdjustmentId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        dualId: string,
        dualScoreAdjustmentId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/duals/${dualId}/adjustments/`;

        if (dualScoreAdjustmentId) {
            url += dualScoreAdjustmentId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[dualScoreAdjustment]') !== -1).length === 0) {
            queryParams.push(`fields[dualScoreAdjustment]=${DualScoreAdjustment.getFields().join(',')}`);
        }

        return queryParams;
    }
}
